// Compte : le bouton rond de la rangée du haut (« ? » grisé tant que le joueur n'a pas de profil, sinon son avatar) et la fenêtre
// de profil qu'il ouvre. Comme ZERO SLANG : un avatar parmi douze, un pseudo (3 à 15 caractères) et la position, qui donne le
// pays et la ville. Pas de mot de passe : le profil signe les scores du Hall of Fame, il est gardé sur l'appareil.
// La même fenêtre sert, en fin de partie, à inscrire son score au Hall of Fame : open({ register: true, score }). Sans position
// (refusée, introuvable), le score reste sur l'appareil.
import { $, show, hide, setTextWithEmoji } from './dom.js';
import { AVATARS, avatarById, normalizePseudo, validatePseudo, isComplete } from '../core/profile.js';
import { flagOf } from '../core/leaderboard.js';
import { PSEUDO_MIN_LENGTH, PSEUDO_MAX_LENGTH } from '../config/constants.js';

// Messages d'erreur du pseudo : le code renvoyé par core/profile.js (ou par submitScore) pointe vers sa clé i18n
const ERROR_KEYS = {
  'invalid-chars': 'profile.error.invalidChars',
  'too-short': 'profile.error.tooShort',
  'too-long': 'profile.error.tooLong',
  'name-taken': 'profile.error.nameTaken',
};

/**
 * @param {object} options
 * @param {(key: string, params?: object) => string} options.t
 * @param {() => Promise<object|null>} options.locate  lieu { countryCode, country, city? } (services/geolocation.js#locatePlace) ;
 *   null si introuvable, rejette si la position est refusée
 * @param {(profile: {pseudo: string, avatar: string, place: object|null}, options: {register: boolean}) => void} options.onSave
 * @param {() => void} [options.onOpen]
 * @param {() => void} [options.onClose]
 */
export function createProfileView({ t, locate, onSave, onOpen = () => {}, onClose = () => {} }) {
  const modal = $('profileModal');
  const chip = $('btnAccount');
  const mark = $('accountMark');
  const grid = $('avatarGrid');
  const inputPseudo = $('inputPseudo');
  const btnLocate = $('btnLocate');
  const locateNote = $('locateNote');
  const btnSave = $('btnSaveProfile');
  const btnClose = $('btnCloseProfile');
  const title = $('profileTitle');
  const sub = $('profileSub');
  let current = null; // profil mémorisé { pseudo, avatar, place }
  let registration = null; // { score } quand la fenêtre inscrit le score de fin de partie, null pour le simple profil
  let avatar = null;
  let place = null;
  let locating = false;
  let placeProblem = null; // 'denied' | 'unknown' : la dernière localisation a échoué
  let serverError = null; // 'name-taken' : le duo emoji + pseudo est déjà pris au Hall of Fame

  const errorText = (code) => (code ? t(ERROR_KEYS[code], { min: PSEUDO_MIN_LENGTH, max: PSEUDO_MAX_LENGTH }) : '');
  const placeLabel = (p) => [p.city, p.country].filter(Boolean).join(', ') + (flagOf(p.countryCode) ? ` ${flagOf(p.countryCode)}` : '');

  grid.replaceChildren(...AVATARS.map(({ id, emoji }) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'avatar-choice';
    button.dataset.avatar = id;
    button.setAttribute('role', 'radio');
    button.textContent = emoji;
    return button;
  }));
  const choices = [...grid.children];

  /** Bouton de compte : « ? » grisé sans profil complet, l'avatar du joueur sinon. */
  function renderChip(profile) {
    current = profile;
    const guest = !isComplete(profile);
    chip.classList.toggle('is-guest', guest);
    mark.textContent = guest ? '?' : avatarById(profile.avatar).emoji;
    const label = guest ? t('profile.chip.guest') : t('profile.chip.named', { pseudo: profile.pseudo });
    chip.title = label;
    chip.setAttribute('aria-label', label);
  }

  function selectAvatar(id) {
    avatar = id;
    choices.forEach((b) => {
      const on = b.dataset.avatar === id;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-checked', String(on));
      b.setAttribute('aria-label', t(`profile.avatar.${b.dataset.avatar}`));
    });
  }

  /** Bouton de position et sa note : partager, localisation en cours, lieu trouvé (à relocaliser), ou ce qui a échoué. */
  function renderPlace() {
    btnLocate.disabled = locating;
    btnLocate.classList.toggle('is-done', !!place);
    const label = locating ? t('profile.place.locating') : place ? t('profile.place.found', { place: placeLabel(place) }) : t('profile.place.share');
    setTextWithEmoji(btnLocate, label);
    let note = '';
    if (placeProblem) note = t(`profile.place.${placeProblem}`);
    else if (place) note = t('profile.place.relocate');
    else if (registration) note = t('profile.place.required');
    locateNote.textContent = note;
  }

  /** Titre, sous-titre et bouton selon le mode : profil, ou inscription au Hall of Fame (avec le score). */
  function renderMode() {
    title.textContent = t(registration ? 'profile.register.title' : 'profile.title');
    btnSave.textContent = t(registration ? 'profile.register.save' : 'profile.save');
    sub.hidden = !registration;
    sub.textContent = registration ? t('profile.register.sub', { score: registration.score }) : '';
  }

  /** Filtrage à la frappe (ni espace ni caractère spécial), compteur, message d'erreur, bouton Enregistrer. */
  function refresh() {
    renderMode();
    renderPlace();
    selectAvatar(avatar);
    const cleaned = normalizePseudo(inputPseudo.value);
    if (cleaned !== inputPseudo.value) inputPseudo.value = cleaned;
    const { valid, errorCode } = validatePseudo(cleaned);
    const shown = serverError ?? (cleaned.length === 0 ? null : errorCode);
    $('pseudoCounter').textContent = `${cleaned.length}/${PSEUDO_MAX_LENGTH}`;
    $('pseudoError').textContent = errorText(shown);
    inputPseudo.classList.toggle('is-invalid', !!shown);
    const ready = valid && !!avatar;
    btnSave.disabled = !ready; // grisé par .btn-cta:disabled
    return ready;
  }

  /**
   * @param {{register?: boolean, score?: number, error?: string|null}} [mode] register : inscrire le score `score` au Hall of
   *   Fame ; error : refus du Hall of Fame à montrer sous le pseudo ('name-taken')
   */
  function open({ register = false, score = 0, error = null } = {}) {
    registration = register ? { score } : null;
    serverError = error;
    inputPseudo.value = current?.pseudo ?? '';
    avatar = current?.avatar ?? null;
    place = current?.place ?? null;
    placeProblem = null;
    refresh();
    show(modal);
    (avatar ? inputPseudo : choices[0]).focus();
    onOpen();
  }

  function close() {
    hide(modal);
    onClose();
  }

  function save() {
    if (!refresh()) {
      (avatar ? inputPseudo : choices[0]).focus();
      return;
    }
    const register = !!registration;
    close();
    onSave({ pseudo: normalizePseudo(inputPseudo.value), avatar, place }, { register });
  }

  async function relocate() {
    if (locating) return;
    locating = true;
    placeProblem = null;
    renderPlace();
    try {
      const found = await locate();
      if (found) place = found;
      else placeProblem = 'unknown';
    } catch {
      placeProblem = 'denied';
    } finally {
      locating = false;
      renderPlace();
    }
  }

  grid.addEventListener('click', (e) => {
    const b = e.target.closest('[data-avatar]');
    if (!b) return;
    serverError = null;
    selectAvatar(b.dataset.avatar);
    refresh();
  });
  chip.addEventListener('click', () => open());
  btnClose.addEventListener('click', close);
  btnSave.addEventListener('click', save);
  btnLocate.addEventListener('click', relocate);
  inputPseudo.addEventListener('input', () => { serverError = null; refresh(); });
  inputPseudo.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !btnSave.disabled) save(); });

  return { renderChip, refresh, open, close, isOpen: () => !modal.hidden };
}
